<script lang="ts" module>
	/** How long every Undo lasts, in milliseconds: --dur-deadline. */
	export const UNDO_MS = 10000;
</script>

<script lang="ts">
	// The design system's Notice (design/components/surfaces/Notice.jsx): a
	// message with a lifetime. One anatomy, a signal dot, the message and an
	// optional action, and three lives:
	//
	//   flash     a confirmation. Floats above the page and fades itself.
	//   deadline  an undo. Pinned to the bottom edge, with a countdown bar that
	//             runs out when the chance does.
	//   standing  a condition, such as being offline. In the flow of the page,
	//             pushing it down, and gone only when the condition is.
	//
	// flash and deadline ride the emphasis surface, which is how they read as
	// above the page with no shadow; the system has none. Entry, exit and the
	// countdown are the design's keyframes, named here and defined in
	// tokens/motion.css. The two timers are the only logic, as in the design.
	//
	// The app adds testIds, and a busy action: while its work is in flight a
	// second tap would be a second attempt, so it stops answering, but it
	// keeps looking live, as the design has every control that is mid-task.

	import { untrack, type Snippet } from 'svelte';
	import Icon from './Icon.svelte';
	import { pressable } from './pressable';

	interface Props {
		life?: 'flash' | 'deadline' | 'standing';
		tone?: 'ok' | 'flag' | 'speed' | 'neutral';
		action?: string;
		onAction?: () => void;
		actionTestId?: string;
		actionBusy?: boolean;
		/** Called when a flash or a deadline runs out. */
		onDismiss?: () => void;
		/**
		 * Names a standing condition with its glyph in place of the dot. The
		 * design allows these two; an overlay passing over the page keeps the dot.
		 */
		icon?: 'offline' | 'clock';
		/** How long a flash or a deadline lives. Defaults to the design's. */
		ms?: number;
		/** Passed through, as the design's components all allow. */
		style?: string;
		testId?: string;
		children: Snippet;
	}

	let {
		life = 'flash',
		tone = 'ok',
		action,
		onAction,
		actionTestId,
		actionBusy = false,
		onDismiss,
		icon,
		ms,
		style,
		testId,
		children,
	}: Props = $props();

	const overlay = $derived(life === 'flash' || life === 'deadline');
	// --dur-deadline and --dur-hold, in milliseconds, for the timers.
	const span = $derived(ms ?? (life === 'deadline' ? UNDO_MS : 2600));
	let leaving = $state(false);

	$effect(() => {
		if (!overlay) return;
		const wait = span;
		// The exit starts slightly early, so the fade ends as the timer does
		// rather than the notice vanishing at full strength.
		const out = setTimeout(() => (leaving = true), Math.max(0, wait - 120));
		const gone = setTimeout(() => untrack(() => onDismiss)?.(), wait);
		return () => {
			clearTimeout(out);
			clearTimeout(gone);
		};
	});
</script>

<div
	class="notice {life} {tone}"
	class:leaving
	role={tone === 'flag' ? 'alert' : 'status'}
	aria-live={tone === 'flag' ? 'assertive' : 'polite'}
	data-ground={overlay ? 'invert' : undefined}
	data-testid={testId}
	{style}
>
	<span class="body">
		{#if !overlay && icon}
			<Icon name={icon} size={18} />
		{:else}
			<span class="dot" aria-hidden="true"></span>
		{/if}
		<span class="message">{@render children()}</span>
		{#if action}
			<button
				type="button"
				class="action"
				data-testid={actionTestId}
				data-press
				aria-busy={actionBusy || undefined}
				onclick={() => {
					if (!actionBusy) onAction?.();
				}}
				{@attach pressable}
			>
				{action}
			</button>
		{/if}
	</span>
	{#if life === 'deadline'}
		<!-- Linear on purpose: a countdown that slows down lies about how much
		     time is left. -->
		<span class="countdown" aria-hidden="true" style:animation-duration="{span}ms"></span>
	{/if}
</div>

<style>
	.body {
		display: flex;
		align-items: center;
		gap: 12px;
		inline-size: 100%;
		font-family: var(--font-sans);
		font-size: 14.5px;
		font-weight: 600;
		animation: bosla-rise var(--dur-enter) var(--ease-out) both;
	}

	.leaving .body {
		animation: bosla-sink var(--dur-exit) var(--ease-in) both;
	}

	.dot {
		inline-size: 7px;
		block-size: 7px;
		border-radius: var(--radius-pill);
		flex: none;
		background: var(--text-muted);
	}

	.ok .dot {
		background: var(--signal-ledger);
	}

	.flag .dot {
		background: var(--signal-flag);
	}

	.speed .dot {
		background: var(--signal-speed);
	}

	.message {
		min-inline-size: 0;
		flex: 1;
		line-height: 1.5;
	}

	.action {
		flex: none;
		white-space: nowrap;
		font-family: var(--font-sans);
		font-size: 13.5px;
		font-weight: 700;
		cursor: pointer;
		border: none;
		background: none;
		padding: 6px 2px;
		min-block-size: var(--ctl-min);
		color: currentColor;
		text-decoration: underline;
		text-underline-offset: 3px;
	}

	/* The two overlays ride the emphasis surface. */
	.flash,
	.deadline {
		position: fixed;
		z-index: var(--z-notice);
		background: var(--surface-invert);
		color: var(--on-ink);
	}

	.flash .action,
	.deadline .action {
		color: var(--on-ink);
	}

	.flash {
		inset-block-end: 68px;
		inset-inline-start: 50%;
		transform: translateX(-50%);
		inline-size: fit-content;
		max-inline-size: var(--notice-max);
		padding: 12px 18px;
		border-radius: var(--radius-btn);
	}

	.deadline {
		inset-block-end: 0;
		inset-inline-start: var(--notice-inset-start);
		inset-inline-end: 0;
		padding: 14px 22px;
		overflow: hidden;
		border-block-start: 1px solid var(--rule-on-ink);
	}

	.countdown {
		position: absolute;
		inset-block-start: 0;
		inset-inline-start: 0;
		block-size: 2px;
		background: var(--signal-speed);
		animation-name: bosla-countdown;
		animation-timing-function: var(--ease-track);
		animation-fill-mode: forwards;
	}

	/* standing is a change to the page, so it sits in it, on its tone's own
	 * ground: going black would read as a modal. */
	.standing {
		padding: 10px 16px;
		border-block-end: 1px solid var(--border-default);
		background: var(--surface-card-header);
		color: var(--text-body);
	}

	.standing .body {
		font-size: 13.5px;
	}

	.standing.flag {
		background: var(--surface-flag);
		color: var(--ink-flag-deep);
		border-block-end-color: var(--border-flag);
	}
</style>
